/**
 * 题库浏览与单题渲染组件
 * ------------------------------------------------------------
 * 「答题界面」在不同页面都要出现（练习、复习、游戏）。
 * 这里把「渲染一道题的输入区 + 读取用户答案」封装成纯函数，
 * 判分统一交给 engines/graders，页面只负责流程。
 *
 * 新增题型：在这里加一个 case，返回 { html, bind(container) → getValue() }。
 */
import { escapeHtml, shuffle } from '../utils/index.js';
import { playItem } from '../services/audioService.js';

/**
 * 渲染题目输入区
 * @param {object} q 题目
 * @param {object} [opts] { disabled:boolean }
 * @returns {string} HTML 片段（含 .quiz-input 容器，data-qid 标识）
 */
export function renderAnswerZone(q, opts = {}) {
  const dis = opts.disabled ? 'disabled' : '';

  switch (q.type) {
    case 'choice':
    case 'judge':
    case 'listen': {
      const options = q.options?.length ? q.options : ['正确', '错误'];
      // judge 固定顺序，其余打乱
      const list = q.type === 'judge' ? options : shuffle(options);
      return `<div class="options" data-qid="${q.id}">
        ${list.map((o, i) => `
          <button class="opt" type="button" data-value="${escapeHtml(o)}" ${dis}>
            <span class="opt-key">${String.fromCharCode(65 + i)}</span>
            <span class="opt-text">${escapeHtml(o)}</span>
          </button>`).join('')}
      </div>`;
    }

    case 'order': {
      const tokens = q.options || q.tokens || [];
      return `<div class="answer-zone" data-qid="${q.id}">
        <div class="token-built" data-role="built"><span class="muted">点下面的单词组成句子</span></div>
        <div class="token-pool" data-role="pool">
          ${shuffle(tokens).map((t, i) => `<button class="token" type="button" data-value="${escapeHtml(t)}" data-i="${i}" ${dis}>${escapeHtml(t)}</button>`).join('')}
        </div>
        <div class="row" style="margin-top:10px">
          <button class="btn btn-ghost btn-sm" type="button" data-role="reset" ${dis}>🔄 重排</button>
          <button class="btn btn-primary" type="button" data-role="submit" ${dis}>提交</button>
        </div>
      </div>`;
    }

    case 'fill': {
      const blanks = (q.answer || []).length || 1;
      return `<div class="answer-zone" data-qid="${q.id}">
        <div class="fill-inputs">
          ${Array.from({ length: blanks }, (_, i) => `
            <label class="fill-item">
              <span class="fill-no">${i + 1}</span>
              <input class="answer-input" type="text" data-fill="${i}"
                placeholder="${i === 0 ? '填第一个空' : `填第 ${i + 1} 个空`}"
                autocomplete="off" autocapitalize="off" spellcheck="false" ${dis}>
            </label>`).join('')}
        </div>
        <button class="btn btn-primary" type="button" data-role="submit" ${dis}>提交</button>
      </div>`;
    }

    default: {
      // spell / transform / correct 及未知题型：单行输入
      return `<div class="answer-zone" data-qid="${q.id}">
        <input class="answer-input" type="text" data-role="input"
          placeholder="${escapeHtml(q.placeholder || '在这里输入答案')}"
          autocomplete="off" autocapitalize="off" spellcheck="false" ${dis}>
        <button class="btn btn-primary" type="button" data-role="submit" ${dis}>提交</button>
      </div>`;
    }
  }
}

/**
 * 绑定交互，返回 { getValue, focus, onSubmit }
 * @param {HTMLElement} host  包含 renderAnswerZone 输出的容器
 * @param {object} q
 * @param {(value:any)=>void} onSubmit
 */
export function bindAnswerZone(host, q, onSubmit) {
  const submit = () => {
    const v = getValue(host, q);
    if (v === null) return;
    onSubmit(v);
  };

  if (q.type === 'choice' || q.type === 'judge' || q.type === 'listen') {
    host.querySelectorAll('.opt').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        onSubmit(btn.dataset.value);
      });
    });
    return { getValue: () => null, focus: () => {}, onSubmit: submit };
  }

  if (q.type === 'order') {
    const built = host.querySelector('[data-role="built"]');
    const pool = host.querySelector('[data-role="pool"]');
    let seq = [];

    const refresh = () => {
      built.innerHTML = seq.length
        ? seq.map((t, i) => `<span class="word-chip" data-idx="${i}">${escapeHtml(t)}</span>`).join('')
        : '<span class="muted">点下面的单词组成句子</span>';
    };
    pool.querySelectorAll('.token').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        seq.push(btn.dataset.value);
        btn.disabled = true;
        btn.classList.add('used');
        refresh();
      });
    });
    built.addEventListener('click', e => {
      const chip = e.target.closest('.word-chip');
      if (!chip) return;
      const idx = Number(chip.dataset.idx);
      const val = seq[idx];
      seq = seq.filter((_, i) => i !== idx);
      // 把词块还回词池
      const back = [...pool.querySelectorAll('.token')].find(b => b.dataset.value === val && b.disabled);
      if (back) { back.disabled = false; back.classList.remove('used'); }
      refresh();
    });
    host.querySelector('[data-role="reset"]')?.addEventListener('click', () => {
      seq = [];
      pool.querySelectorAll('.token').forEach(b => { b.disabled = false; b.classList.remove('used'); });
      refresh();
    });
    host.querySelector('[data-role="submit"]')?.addEventListener('click', () => {
      if (!seq.length) return;
      onSubmit(seq);
    });
    return { getValue: () => seq.slice(), focus: () => {}, onSubmit: submit };
  }

  if (q.type === 'fill') {
    const inputs = [...host.querySelectorAll('[data-fill]')];
    inputs.forEach((inp, i) => {
      inp.addEventListener('keydown', e => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (i < inputs.length - 1) inputs[i + 1].focus();
        else submit();
      });
    });
    host.querySelector('[data-role="submit"]')?.addEventListener('click', submit);
    setTimeout(() => inputs[0]?.focus(), 80);
    return {
      getValue: () => inputs.map(i => i.value).join('|||'),
      focus: () => inputs[0]?.focus(),
      onSubmit: submit,
    };
  }

  const input = host.querySelector('[data-role="input"]');
  input?.addEventListener('keydown', e => { if (e.key === 'Enter') submit(); });
  host.querySelector('[data-role="submit"]')?.addEventListener('click', submit);
  setTimeout(() => input?.focus(), 80);
  return { getValue: () => input?.value ?? '', focus: () => input?.focus(), onSubmit: submit };
}

/** 统一取值（供 bindAnswerZone 内部与外部复用） */
export function getValue(host, q) {
  if (q.type === 'order') {
    return [...host.querySelectorAll('.word-chip')].map(c => c.textContent);
  }
  if (q.type === 'fill') {
    const vals = [...host.querySelectorAll('[data-fill]')].map(i => i.value.trim());
    if (vals.every(v => !v)) return null;   // 全空不提交
    return vals.join('|||');
  }
  const input = host.querySelector('[data-role="input"]');
  const v = input?.value?.trim();
  return v ? v : null;
}

/**
 * 题干区渲染（含发音按钮、提示）
 * @param {object} q
 * @param {object} [opts] { showHint:boolean }
 */
export function renderPrompt(q, opts = {}) {
  const isListen = q.type === 'listen';
  const audioText = q.audio || q.answer?.[0] || '';
  const prompt = isListen ? '🔊 听发音，选出你听到的内容' : String(q.prompt || '').replace(/\n/g, '<br>');
  return `<div class="quiz-prompt">
    <div class="prompt-text">${prompt}</div>
    <div class="prompt-tools">
      ${audioText ? `<button class="btn btn-sm btn-round" type="button" data-role="speak">🔊 ${isListen ? '再听一次' : '听发音'}</button>` : ''}
      ${q.hint && opts.showHint ? `<span class="prompt-hint">💡 ${escapeHtml(q.hint)}</span>` : ''}
    </div>
  </div>`;
}

/** 绑定发音按钮：优先走内置 MP3（q.audioUrl），没传才走 TTS */
export function bindSpeak(host, q) {
  const btn = host.querySelector('[data-role="speak"]');
  if (!btn) return;
  // 优先用题目自带的 audioUrl（独立音频文件）；否则传 text 让 audioService 内部 fallback TTS
  const audioRel = q.audioUrl || '';
  const text = q.audio || q.answer?.[0] || q.prompt || '';
  if (!audioRel && !text) return;
  btn.addEventListener('click', () => {
    playItem({ audio: audioRel, text, lang: 'en-US' });
  });
  // 不再自动播放（setTimeout 会脱离用户手势，容易被浏览器拦截），让用户主动点
}

/** 判定反馈条 */
export function renderFeedback(result, q) {
  const ok = result.correct;
  return `<div class="feedback ${ok ? 'ok' : 'no'}">
    <div class="feedback-head">${ok ? '✓ 答对了' : '× 答错了'}</div>
    ${!ok && result.expected ? `<div class="feedback-line">正确答案：<b>${escapeHtml(result.expected)}</b></div>` : ''}
    ${q.explain ? `<div class="feedback-line muted">${escapeHtml(q.explain).replace(/\n/g, '<br>')}</div>` : ''}
  </div>`;
}
